import React, { useState, useMemo, useEffect, useCallback, Suspense } from "react";
import AdminTableView from "./AdminTableView";
import Loading from "../swt-loading";
import { formatDateTimeUTC } from "../UtilityFunctions";
import { rowsToData, columnsToExcelHeaders } from "./TableHelpers";
import * as S from "../../../../styles/core-styles/AdminTools-styles";
import { processApiResponse } from "../../utility-components/ConformUnits";

//code-splitting imports
const ExcelDownloadButton = React.lazy(() => import("./ExcelDownloadButton"));


const LOW_SPACE_THRESHOLD = 15; //space left on db storage in pct
const CONNECTIONS_SWAMPED_THRESHOLD = 0.35; // workers over max_connects ratio below which is a problem
const PROBLEM_COLOR = "#e76f51";
const NOMINAL_COLOR = "#2a9d8f";

export default function NodeStatusTable(props){

    const { apiURL, user } = props;
    const [nodes, setNodes] = useState(null);
    const [now, setNow] = useState(new Date());

    const boolSort = useMemo(() => (rowA, rowB, columnId) => {
        const a = rowA.values[columnId].props.bool;
        const b = rowB.values[columnId].props.bool;
        return a > b ? 1 : -1;
    },[]);

    const statusSort = useMemo(() => (rowA, rowB, columnId) =>{
        const a = rowA.values[columnId].props.status;
        const b = rowB.values[columnId].props.status;
        return a > b ? 1 : -1;
    }, []);

    const dateTimeSort = useMemo(() => (rowA, rowB) => {
        const a = rowA.original.updated_original;
        const b = rowB.original.updated_original;
        return a > b ? 1 : -1;
    },[]);

    const NodeStatus = (workers, max_connections, space, excelFormat) => {
        const low_space = space < LOW_SPACE_THRESHOLD ? true : false;
        const connects_swamped = workers/max_connections > CONNECTIONS_SWAMPED_THRESHOLD ? true : false;
        //if(some medium prob level)return(<S.CDRStatusMarker status={1} backgroundColor="#e9c46a" />);
        if(low_space || connects_swamped){
            if(excelFormat) {
                return 'Red'
            } else {
                return(<S.CDRStatusMarker status={0}  backgroundColor={PROBLEM_COLOR} />);
            }
        } else {
            if(excelFormat) {
                return 'Green'
            } else {
                return(<S.CDRStatusMarker status={2} backgroundColor = {NOMINAL_COLOR} />);
            }
        }
    }

    const columns = [
        {Header: "Status", accessor: "status", sortType: statusSort, width: 120, showInDownload: true},
        {Header: "Name", accessor: "name", sortType:"basic", width: 150, showInDownload: true},
        {Header: "IP Address", accessor: "address", sortType:"basic", width: 200, showInDownload: true},
        {Header: "Space", accessor: "space", sortType:"basic", width: 120, showInDownload: true},
        {Header: "Workers", accessor: "workers", sortType:"basic", width: 120, showInDownload: true},
        {Header: "Max Connections", accessor: "max_connections", sortType:"basic", width: 150, showInDownload: true},
        {Header: "Monitor", accessor: "monitor", sortType:boolSort, width: 120, showInDownload: true},
        {Header: "Updated", accessor: "updated", sortType:dateTimeSort, showInDownload: true},
    ];

    const BoolTableCell = useCallback((props) =>
    <input
        className="swt-admin-table-input"
        type="checkbox"
        id={props.id} 
        name={props.name}
        accessor={props.accessor} 
        style={{color: props.bool ? "black" : "transparent"}} 
        onChange={props.handleOnClick}
        checked={(props.bool === null || typeof props.bool === "undefined") ? false : props.bool}
        disabled={typeof props.bool === "undefined" ? true : false}
    />,[]);

    useEffect(() => {
        const url = `${apiURL}getNodeStatus`;
        fetch(`${url}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        })
            .then((resp) => {
                return resp.json();
            })
            .then((data) => {
                if (data.status === "error") 
                    alert("There was a server error during your request.");
                else {
                    data.data.map((d) => {
                        d.updated_original = JSON.parse(JSON.stringify(d.updated))
                        d.updated = formatDateTimeUTC(d.updated)
                        d = processApiResponse(user.userSettings, d, false);
                        return d;
                    })
                    setNodes(data.data);  
                }
            })
            .catch((error) => console.error("Error: " + error));
    }, [apiURL, user.token, now, user.userSettings]);

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(new Date());
          }, 10000);
          return () => clearInterval(interval);
    }, []);
    
    const mappedNodes = useMemo(() => {
        if(!nodes)return null;
        if(nodes.length < 1)return [];

        const ns = JSON.parse(JSON.stringify(nodes)); 

        return ns.filter((c) => {
            c.updated = c.updated ? `${c.updated.date} - ${c.updated.time}` : null;
            c.status = NodeStatus(c.workers, c.max_connections, c.space);
            c.monitor = <BoolTableCell id={c.name} accessor={"monitor"} bool={c.monitor} handleOnClick={function(el){}}/>;
            return c;
        })
    }, [nodes]);

    const formattedExcelData = useMemo(() => {
        if(!nodes) return [];
        return nodes.map((n) => {
            n.updated = n.updated ? `${n.updated.date} - ${n.updated.time}` : null;
            n.status = NodeStatus(n.workers, n.max_connections, n.space, true);
            return n;
        })
    },[nodes])

    return(
        <div>
            {!mappedNodes && <Loading />}
            {mappedNodes &&
            <>
            <S.CDRTableHeaderContainer>
                <Suspense fallback={<div></div>}>
                <ExcelDownloadButton
                 downloadType={"cdrNodeStatusDownload"}
                 columns={columnsToExcelHeaders(columns)}
                 data={rowsToData(columns, formattedExcelData, ((a,b) => a.status < b.status ? 1 : -1))}
                 userSettings={props.user.userSettings}
                />
                </Suspense>
            </S.CDRTableHeaderContainer>
            <AdminTableView 
                columns={columns}
                data={mappedNodes}
                sortBy={{id: "status", desc: false}}
                stickyCols={2}
            />
            </>
            }
        </div>
    )
}